// Metadata pairs, execution condition rules, the AND/OR selector and the
// auto-enable choice.

import React from 'react';
import PropTypes from 'prop-types';
import { Grid, RadioGroup } from '@decisiv/ui-components';
import { H4, P } from '@decisiv/ui-components/lib/components/Typography';
import { t, Trans } from '@lingui/macro';

import { FormDivider, FormGroup, SectionRow } from '../StyledComponents';
import ActionMetadataNotice from '../../../components/ActionMetadataNotice';
import EditExecutionConditions from '../EditExecutionConditions';
import EditMetadata from '../EditMetadata';
import ExecutionConditionsInfoContent from '../ExecutionConditionsInfoContent';
import FeatureInfoDrawer from '../FeatureInfoDrawer';
import MetadataInfoContent from '../MetadataInfoContent';

const MetadataAndConditions = ({
  edit,
  errors,
  actionMetadataSchema = null,
  metadata,
  metadataHandlers,
  executionConditions,
  executionConditionsHandlers,
  payloadSources = [],
  onConditionTypeChange,
  autoEnable,
  onAutoEnableChange,
}) => (
  <>
    <FormDivider />
    <SectionRow>
      <Grid.Column span="2">
        <FeatureInfoDrawer
          heading={<Trans>Metadata</Trans>}
          ariaLabel={t`More information about Metadata`}
          wide
        >
          <MetadataInfoContent />
        </FeatureInfoDrawer>
      </Grid.Column>
      <Grid.Column>
        <ActionMetadataNotice metadataSchema={actionMetadataSchema} />
        <FormGroup key="metadata">
          <EditMetadata
            metadata={metadata}
            handlers={metadataHandlers}
            editDisabled={edit}
            errorsArray={errors}
          />
        </FormGroup>
      </Grid.Column>
    </SectionRow>

    <FormDivider />
    <SectionRow>
      <Grid.Column span="2">
        <FeatureInfoDrawer
          heading={<Trans>Execution Conditions</Trans>}
          ariaLabel={t`More information about Execution Conditions`}
          wide
        >
          <ExecutionConditionsInfoContent />
        </FeatureInfoDrawer>
      </Grid.Column>
      <Grid.Column>
        <FormGroup key="execution_conditions">
          <EditExecutionConditions
            executionConditions={executionConditions}
            handlers={executionConditionsHandlers}
            editDisabled={edit}
            errorsArray={errors}
            payloadSources={payloadSources}
          />
        </FormGroup>
      </Grid.Column>
    </SectionRow>

    <SectionRow>
      <Grid.Column span="2">
        <P weight="semibold" shade={1} style={{ marginLeft: 16 }}>
          <Trans>Execution Conditions Type</Trans>
        </P>
      </Grid.Column>
      <Grid.Column>
        <FormGroup key="execution_conditions_type">
          <RadioGroup
            disabled={edit}
            name="execution_condition_type"
            items={[
              { label: t`Match ALL conditions (AND)`, value: 'and' },
              { label: t`Match ANY condition (OR)`, value: 'or' },
            ]}
            value={executionConditions[0].condition_type}
            onChange={(e) => onConditionTypeChange(e.target.value)}
          />
        </FormGroup>
      </Grid.Column>
    </SectionRow>

    <SectionRow>
      <Grid.Column span="2">
        <H4>
          <Trans>Auto Enable</Trans>
        </H4>
      </Grid.Column>
      <Grid.Column>
        <P shade={1} size="small" marginBottom={1}>
          <Trans>
            Mark this as a core integration so it is automatically enabled by
            default when a new location is created.
          </Trans>
        </P>
        <FormGroup key="auto_enable">
          <RadioGroup
            disabled={edit}
            name="auto_enable"
            items={[
              { label: t`Yes`, value: 'true' },
              { label: t`No`, value: 'false' },
            ]}
            value={String(autoEnable)}
            onChange={(e) => onAutoEnableChange(e.target.value === 'true')}
          />
        </FormGroup>
      </Grid.Column>
    </SectionRow>
  </>
);

MetadataAndConditions.propTypes = {
  edit: PropTypes.bool.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  errors: PropTypes.object.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  actionMetadataSchema: PropTypes.object,
  // eslint-disable-next-line react/forbid-prop-types
  metadata: PropTypes.array.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  metadataHandlers: PropTypes.object.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  executionConditions: PropTypes.array.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  executionConditionsHandlers: PropTypes.object.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  payloadSources: PropTypes.array,
  onConditionTypeChange: PropTypes.func.isRequired,
  autoEnable: PropTypes.bool.isRequired,
  onAutoEnableChange: PropTypes.func.isRequired,
};
export default MetadataAndConditions;
